import React from 'react';
import { X } from 'lucide-react';
import type { Toast as ToastType } from '@/contexts/ToastContext';

interface ToastProps {
  toast: ToastType;
  onDismiss: (id: string) => void;
}

export function Toast({ toast, onDismiss }: ToastProps) {
  const backgrounds = {
    success: 'var(--alert-success-bg)',
    error: 'var(--alert-error-bg)',
    warning: 'var(--alert-warning-bg)',
    info: 'var(--alert-info-bg)',
  };

  const borders = {
    success: 'var(--alert-success-border)',
    error: 'var(--alert-error-border)',
    warning: 'var(--alert-warning-border)',
    info: 'var(--alert-info-border)',
  };

  const colors = {
    success: 'var(--alert-success-text)',
    error: 'var(--alert-error-text)',
    warning: 'var(--alert-warning-text)',
    info: 'var(--alert-info-text)',
  };

  const style: React.CSSProperties = {
    backgroundColor: backgrounds[toast.type],
    border: `1px solid ${borders[toast.type]}`,
    color: colors[toast.type],
    padding: '1rem 1.5rem',
    borderRadius: 'var(--radius)',
    marginBottom: '0.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: 'var(--shadow)',
    minWidth: '300px',
    maxWidth: '500px',
  };

  return (
    <div style={style} role="alert" aria-live="polite">
      <span>{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          marginLeft: '1rem',
          color: colors[toast.type],
          opacity: 0.7,
          display: 'flex',
          alignItems: 'center',
        }}
        aria-label="Dismiss"
      >
        <X size={18} aria-hidden="true" />
      </button>
    </div>
  );
}

export function ToastContainer({ toasts, onDismiss }: { toasts: ToastType[]; onDismiss: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '1rem',
        right: '1rem',
        zIndex: 9999,
      }}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
